export enum ECurrency {
    CANADIAN = 'canadian'
}

export enum EOrderStatus {
    PROGRESS = 'In progress',
    LOCKED = 'locked',
    FINISH = 'finish'
}

export const enum ECardType {
    PHYSICAL = 'primary',
    VIRTUAL = 'virtual'
}

export enum ETransactionType {
    CARD = 'Payment',
    BANK_ACCOUNT = 'Transfer from bank'
}

export enum ECreditCardBrands {
    AMEX = 'amex',
    VISA = 'visa',
    MASTERCARD = 'mastercard',
    DISCOVER = 'discover'
}

export enum EFundsOperation {
    WALLET_CREDIT_TRANSFER = '1_1_4',
    WALLET_DEBIT_TRANSFER = '1_2_4',
    WALLET_DEBIT_PAYMENT = '1_2_6',
    WALLET_CREDIT_PAYMENT = '1_1_6',
    WALLET_CREDIT_REQUEST = '1_1_5',
    WALLET_DEBIT_REQUEST = '1_2_5',
    WALLET_CREDIT_CARD_DEPOSIT = '1_1_9',
    WALLET_CREDIT_CARD_SERVICE = '1_1_8',
    WALLET_DEBIT_CASHBACK = '1_2_7',
    WALLET_DEBIT_PAYOUT = '1_2_16',
    WALLET_CREDIT_PAYOUT = '1_1_16',
    // Merchant Connect/PWX order refund (WalletMethod/DebitType/ActionRefund). Backend:
    // main-api-webhooks internal/http/modules/orders/orders.go MgmtRefundOrder ("Only
    // UserTypeBusiness accounts may issue refunds (403 otherwise)") and
    // internal/app/services/merchant_refund_service.go:274-275/380-381 ("1_2_1 is the
    // merchant refund fee/limit key"). Distinct from the P2P send/request reversal in
    // funds_refund.go, which derives its fee from the original transaction and has no
    // independent key — that path is correctly absent from this admin screen.
    WALLET_DEBIT_REFUND = '1_2_1',
    PAY_BILL_CREDIT_PAYMENT = '2_1_6',
    E_TRANSFER_CREDIT_TRANSFER = '3_1_4',
    E_TRANSFER_DEBIT_TRANSFER = '3_2_4',
    E_TRANSFER_DEBIT_REQUEST = '3_2_5',
    EFT_CREDIT_WITHDRAWAL = '4_1_3',
    EFT_DEBIT_DEPOSIT = '4_2_2',
    EPS_DEBIT_DEPOSIT = '5_2_2',
    CARD_CREDIT_WITHDRAWAL = '6_1_10',
    CARD_DEBIT_DEPOSIT = '6_2_9',
    GIFT_CARD_CREDIT_GIFT_CARD = '8_1_13',
    GIFT_CARD_CREDIT_RESERVE = '8_1_15',
    GIFT_CARD_DEBIT_GIFT_CARD_REDEEM = '8_2_14'
}

// Operations an Individual user can never reach — the backend enforces a
// UserTypeBusiness-only guard (403 for individuals) on every key listed here, so
// admin config screens must gate these rows to the Business tab only. Every entry
// must be traceable to a concrete backend guard; do not add a key "for symmetry"
// without confirming it — an operation individuals can legitimately use must stay
// visible on both tabs (hiding it would remove a control they actually need).
//
// - GIFT_CARD_CREDIT_GIFT_CARD / GIFT_CARD_CREDIT_RESERVE: an Individual can never
//   issue or reserve (activate) a XEPPT gift card (backend-enforced). Redeem
//   (GIFT_CARD_DEBIT_GIFT_CARD_REDEEM) is deliberately NOT listed — individuals DO
//   redeem gift cards.
// - WALLET_DEBIT_REFUND (1_2_1): merchant Connect/PWX order refund. Backend:
//   orders.go MgmtRefundOrder ("Only UserTypeBusiness accounts may issue refunds").
// - WALLET_DEBIT_PAYMENT (1_2_6): the MERCHANT's Connect/PWX "receive" leg. The
//   consumer/buyer leg is WALLET_CREDIT_PAYMENT (1_1_6) — NOT listed here, since
//   POST /connect/charge (main-api-webhooks internal/http/modules/connect/charge.go)
//   authenticates any user type as the paying consumer, no business-type guard.
//   1_2_6 itself is merchant-only because `session.BusinessUserID` (the receiving
//   side) is only ever populated by a business-gated session-creation endpoint
//   (sandbox_connect_session.go, dev_demo_connect_session.go: both
//   `au.TypeIs(models.UserTypeBusiness)`; live POST /connect/sessions:
//   `middleware.APIAuth(models.UserTypeBusiness)` in internal/http/modules/api/api.go).
// - WALLET_CREDIT_PAYOUT (1_1_16): the MERCHANT's outbound payout leg — only a
//   UserTypeBusiness caller can create a payout (payout_create_business.go:65
//   `au.TypeIs(models.UserTypeBusiness)`, dispatched from CreatePayoutDispatch).
//   Gate on the KEY, not the row label: 1_1_16 is
//   WalletMethod/CreditType/ActionPayout = "merchant sends money out" per
//   models/transaction_types.go's CreditType doc comment and
//   merchant_payout_service.go:179. WALLET_DEBIT_PAYOUT (1_2_16) is the
//   customer/recipient leg, resolved from the merchant's customer-link table with
//   no user-type restriction (an Individual can receive a payout) — it must stay
//   ungated. (The payout row labels in limits/index.tsx and loyalty/index.tsx now
//   follow the CREDIT=out / DEBIT=in convention, matching these keys.)
export const BUSINESS_ONLY_OPERATIONS: EFundsOperation[] = [
    EFundsOperation.GIFT_CARD_CREDIT_GIFT_CARD,
    EFundsOperation.GIFT_CARD_CREDIT_RESERVE,
    EFundsOperation.WALLET_DEBIT_REFUND,
    EFundsOperation.WALLET_DEBIT_PAYMENT,
    EFundsOperation.WALLET_CREDIT_PAYOUT
];

// Mirror of BUSINESS_ONLY_OPERATIONS: operations a Business user can never reach,
// so admin config screens must gate these rows to the Personal/Individual tab
// only. Same rule applies — every entry must trace to a concrete backend guard.
//
// - WALLET_DEBIT_CASHBACK (1_2_7): cashback redeem is the consumer
//   shopping-rewards feature ("redeem to your personal XEPPT wallet") —
//   Individual-only. Backend: internal/app/actions/cashback_redeem.go:26-27
//   (`if user.Type != models.UserTypeIndividual { return
//   ErrUserTypeIndividualAllowed }`), fails closed before any cashback lookup or
//   wallet credit. The same key's per-transaction min/max is enforced right after
//   via CheckUserLimits (cashback_redeem.go:49-56, WalletMethod/DebitType/
//   ActionCashBack), and points are awarded off this key's plan by
//   cashback_service.go:304-364. A Business caller can never reach any of this.
export const INDIVIDUAL_ONLY_OPERATIONS: EFundsOperation[] = [
    EFundsOperation.WALLET_DEBIT_CASHBACK
];
